import type { JobDTO } from "./job";
import { AuthenticationFailedError, Session } from "./session";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly url: string,
    /** Whatever the server sent back — often empty, since the backend rarely bodies its errors. */
    readonly body: unknown,
  ) {
    super(`${status} ${url}`);
    this.name = "ApiError";
  }

  /** The role gate rejected this verb for the current account. */
  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  /** Serialised as JSON unless `raw` is set. */
  body?: unknown;
  /** Raw payload for the octet-stream upload endpoints. */
  raw?: Blob | ArrayBuffer | ReadableStream;
  query?: QueryParams;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** Set for endpoints that answer with a file rather than JSON. */
  responseType?: "json" | "blob" | "text" | "void";
}

export type QueryValue = string | number | boolean | null | undefined | ReadonlyArray<string | number>;
export type QueryParams = Record<string, QueryValue>;

/**
 * Builds a query string the way the backend expects: an array becomes a
 * repeated parameter (`?ids=1&ids=2`), which Spring binds to a `List` exactly
 * as it does a comma-joined one.
 *
 * Repeated rather than joined because a value may legitimately contain a
 * comma. A grid filtering on a catalogue called "Prints, Framed" sends one
 * value; joined, the server would read two, and answer with rows from neither.
 *
 * An empty array contributes nothing, so an absent filter and a cleared one
 * produce the same request — and therefore the same cache key.
 */
export function buildQuery(params: QueryParams | undefined): string {
  if (!params) return "";
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined || value === "") continue;
    if (Array.isArray(value)) {
      // Blank entries are meaningful in a from/to pair — ["", "500"] is "up to
      // 500" — so they are kept, and only a wholly empty array is dropped.
      for (const entry of value) search.append(key, String(entry));
      continue;
    }
    search.set(key, String(value));
  }
  const query = search.toString();
  return query ? `?${query}` : "";
}

export class HttpClient {
  constructor(private readonly session: Session) {}

  /**
   * One request, with a single transparent retry after a token refresh.
   *
   * A 401 means the 15-minute access token expired mid-session. We refresh
   * once and replay; if the replay also 401s, the session is genuinely over
   * and the app is told to send the user back to login.
   */
  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const url = `${this.session.basePath}${path}${buildQuery(options.query)}`;

    const send = async (): Promise<Response> => {
      const headers: Record<string, string> = { ...options.headers };

      const token = this.session.token;
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      let body: BodyInit | undefined;
      if (options.raw !== undefined) {
        headers["Content-Type"] ??= "application/octet-stream";
        body = options.raw as BodyInit;
      } else if (options.body !== undefined) {
        headers["Content-Type"] = "application/json";
        body = JSON.stringify(options.body);
      }

      const init: RequestInit = {
        method: options.method ?? "GET",
        headers,
        credentials: "same-origin",
        ...(body !== undefined ? { body } : {}),
        ...(options.signal ? { signal: options.signal } : {}),
      };

      return fetch(url, init);
    };

    let response = await send();

    if (response.status === 401) {
      try {
        await this.session.refresh();
      } catch {
        this.session.abandon();
        throw new AuthenticationFailedError();
      }
      response = await send();
      if (response.status === 401) {
        this.session.abandon();
        throw new AuthenticationFailedError();
      }
    }

    if (!response.ok) {
      throw new ApiError(response.status, url, await safeBody(response));
    }

    return (await readBody(response, options.responseType ?? "json")) as T;
  }

  get<T>(path: string, options?: Omit<RequestOptions, "method" | "body">): Promise<T> {
    return this.request<T>(path, { ...options, method: "GET" });
  }

  post<T>(path: string, body?: unknown, options?: Omit<RequestOptions, "method">): Promise<T> {
    return this.request<T>(path, { ...options, method: "POST", body });
  }

  put<T>(path: string, body?: unknown, options?: Omit<RequestOptions, "method">): Promise<T> {
    return this.request<T>(path, { ...options, method: "PUT", body });
  }

  delete<T>(path: string, options?: Omit<RequestOptions, "method">): Promise<T> {
    return this.request<T>(path, { ...options, method: "DELETE" });
  }

  /**
   * Starts a document export.
   *
   * Exports are background jobs: the response is the job to watch, and the file
   * itself is collected from the job center once it succeeds. Nothing here waits
   * for the document — the accounting workbook alone can take minutes to build,
   * which is exactly what the old blocking download could not survive.
   */
  job(path: string, options: RequestOptions = {}): Promise<JobDTO> {
    return this.request<JobDTO>(path, { ...options, method: options.method ?? "POST" });
  }

  /**
   * Downloads a generated document. The export endpoints are POSTs that answer
   * with bytes and a Content-Disposition filename, so the filename comes off
   * the response rather than being guessed by the caller.
   */
  async download(
    path: string,
    options: RequestOptions = {},
  ): Promise<{ blob: Blob; filename: string }> {
    const url = `${this.session.basePath}${path}${buildQuery(options.query)}`;
    const headers: Record<string, string> = { ...options.headers };
    const token = this.session.token;
    if (token) headers.Authorization = `Bearer ${token}`;
    if (options.raw !== undefined) headers["Content-Type"] ??= "application/octet-stream";
    else if (options.body !== undefined) headers["Content-Type"] = "application/json";

    const response = await fetch(url, {
      method: options.method ?? "POST",
      headers,
      credentials: "same-origin",
      // `raw` is the upload-in, file-out case: the parcel report goes up as
      // bytes and the normalised CSV comes back as the response.
      ...(options.raw !== undefined
        ? { body: options.raw as BodyInit }
        : options.body !== undefined
          ? { body: JSON.stringify(options.body) }
          : {}),
      ...(options.signal ? { signal: options.signal } : {}),
    });

    if (!response.ok) {
      throw new ApiError(response.status, url, await safeBody(response));
    }

    return {
      blob: await response.blob(),
      filename: filenameFrom(response.headers.get("Content-Disposition")),
    };
  }
}

async function readBody(response: Response, type: NonNullable<RequestOptions["responseType"]>) {
  if (type === "void" || response.status === 204) return undefined;
  if (type === "blob") return response.blob();
  if (type === "text") return response.text();

  // Several endpoints answer 200 with an empty body (the boolean-returning
  // updates, for one), which would make response.json() throw.
  const text = await response.text();
  return text ? JSON.parse(text) : undefined;
}

async function safeBody(response: Response): Promise<unknown> {
  try {
    const text = await response.text();
    if (!text) return null;
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  } catch {
    return null;
  }
}

function filenameFrom(header: string | null): string {
  if (!header) return "download";
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utf8?.[1]) return decodeURIComponent(utf8[1]);
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain?.[1] ?? "download";
}
